import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { downloadRefundVoucher, printRefundVoucher, useOrders, useRefundable, useRequestRefund } from '@/api/hooks'
import type { Refund } from '@/api/types'
import { ErrorBanner, formatMoney } from '@/components/common'

/**
 * Raising a refund on an order that has been overpaid.
 *
 * <p>Pick the order and the excess is worked out and offered: paid, less what
 * the order comes to, less anything already raised. More than that is refused
 * by the server, because refunding money that never came in is the one mistake
 * here that cannot be put right by editing a figure.</p>
 */
export function RaiseRefundDialog({
  open,
  fixedOrderId,
  onClose,
}: {
  open: boolean
  /** Set when raised from an order, where there is nothing to choose. */
  fixedOrderId?: string
  onClose: () => void
}) {
  const [pickedOrder, setPickedOrder] = useState('')
  const orderId = fixedOrderId ?? pickedOrder
  const [amount, setAmount] = useState('')
  const [withheld, setWithheld] = useState('')
  const [mode, setMode] = useState('BANK_TRANSFER')
  const [reason, setReason] = useState('')
  // Kept after it is raised, because the voucher is wanted at that moment and
  // not after a hunt through another screen for it.
  const [raised, setRaisedRefund] = useState<Refund | null>(null)
  // Only needed when there is an order to choose; opened from one, there is not.
  const { data: orders } = useOrders({ page: 0, size: 100 }, !fixedOrderId)
  const { data: excess } = useRefundable(orderId || undefined)
  const raise = useRequestRefund()

  // Offered rather than demanded: the figure is the server's, and typing it
  // again by hand is only a chance to get it wrong.
  const propose = (id: string) => {
    setPickedOrder(id)
    setAmount('')
    setReason('')
  }

  const close = () => {
    setPickedOrder('')
    setAmount('')
    setWithheld('')
    setReason('')
    setRaisedRefund(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{raised ? `Raised ${raised.refundNumber}` : 'Raise a refund'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={raise.error} />
        {raised ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            {formatMoney(raised.netPayable)} to go back to {raised.customerName}
            {raised.status === 'APPROVED'
              ? '. Approved — print the voucher for whoever makes the transfer.'
              : '. It is waiting for approval; the voucher says so on its face.'}
          </Alert>
        ) : null}
        <Stack spacing={2} sx={{ mt: 1 }} display={raised ? 'none' : undefined}>
          {fixedOrderId ? null : (
            <TextField
              select
              fullWidth
              size="small"
              label="Order"
              value={pickedOrder}
              onChange={(event) => propose(event.target.value)}
            >
              {(orders?.content ?? []).map((order) => (
                <MenuItem key={order.id} value={order.id}>
                  {order.orderNumber} — {order.customerName}
                </MenuItem>
              ))}
            </TextField>
          )}

          {excess ? (
            <Alert severity={excess.refundable > 0 ? 'info' : 'warning'}>
              {excess.orderNumber} comes to {formatMoney(excess.totalAmount)} and{' '}
              {formatMoney(excess.paidAmount)} has been received
              {excess.alreadyRefunded > 0
                ? `, of which ${formatMoney(excess.alreadyRefunded)} is already being refunded`
                : ''}
              .{' '}
              {excess.refundable > 0 ? (
                <>
                  <strong>{formatMoney(excess.refundable)}</strong> may be refunded.{' '}
                  <Button size="small" onClick={() => setAmount(String(excess.refundable))}>
                    Use this
                  </Button>
                </>
              ) : (
                'Nothing is owed back on it.'
              )}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Amount"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Withheld"
              value={withheld}
              onChange={(event) => setWithheld(event.target.value)}
              helperText="A cancellation charge, if any"
            />
          </Stack>

          <TextField
            select
            fullWidth
            size="small"
            label="How it goes back"
            value={mode}
            onChange={(event) => setMode(event.target.value)}
          >
            {['BANK_TRANSFER', 'UPI', 'CASH', 'CHEQUE', 'GATEWAY'].map((value) => (
              <MenuItem key={value} value={value}>
                {value.replace('_', ' ').toLowerCase()}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            label="Why"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Excess received against the order"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {raised ? (
          <>
            <Button variant="contained" onClick={() => void printRefundVoucher(raised)}>
              Print voucher
            </Button>
            <Button onClick={() => void downloadRefundVoucher(raised)}>PDF</Button>
            <Button onClick={close}>Done</Button>
          </>
        ) : (
          <>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!orderId || !Number(amount) || !reason.trim() || raise.isPending}
          onClick={() =>
            raise.mutate(
              {
                orderId,
                body: {
                  amount: Number(amount),
                  deductionAmount: withheld ? Number(withheld) : 0,
                  refundMode: mode,
                  reason: reason.trim(),
                },
              },
              { onSuccess: setRaisedRefund },
            )
          }
        >
          Raise refund
        </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
